/**
 * PhiNotice — the audit workspace's no-PHI reminder plus the Report PHI
 * button. Plain words, always visible: never paste or upload member
 * information here; universes go through the Scrubber; if PHI slips in,
 * one click alerts Compliance to purge it.
 */
import React, { useState } from 'react';

const API = process.env.REACT_APP_API_URL || '';

export default function PhiNotice({ auditId }) {
  const [reporting, setReporting] = useState(false);
  const [sent, setSent] = useState(false);
  const [where, setWhere] = useState('');
  const [busy, setBusy] = useState(false);

  const report = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/phi-report`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ where }),
      }).then((r) => r.json());
      if (r.success) { setSent(true); setReporting(false); setWhere(''); }
    } catch { /* keep the form open so they can retry */ }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-bold uppercase tracking-wide text-amber-700">No-PHI zone</span>
        <span className="text-[12px] text-amber-800 flex-1 min-w-[240px]">
          Please keep member information out of this workspace (names, DOBs, SSNs, Medicare numbers). Refer to cases by
          sample number; member-level universes go through the Scrubber and only the results belong here. If PHI does slip
          in, that is okay, it happens. One click below tells Compliance so it stays protected, and reporting never gets
          anyone in trouble.
        </span>
        {sent ? (
          <span className="text-[12px] font-semibold text-emerald-700">Reported — Compliance alerted.</span>
        ) : (
          <button
            onClick={() => setReporting(v => !v)}
            className="text-[12px] font-semibold text-amber-800 border border-amber-300 rounded-lg px-2.5 py-1 hover:bg-amber-100"
            title="If PHI made it into this audit anywhere, report it so Compliance can purge it."
          >
            Report PHI
          </button>
        )}
      </div>
      {reporting && !sent && (
        <div className="mt-2 flex gap-2">
          <input
            value={where} onChange={(e) => setWhere(e.target.value)}
            placeholder="Where did you see it? (e.g. the note dated 7/9, the vendor file) — do not repeat the PHI itself"
            className="flex-1 text-[12px] border border-amber-300 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:border-amber-500"
          />
          <button onClick={report} disabled={busy} className="text-[12px] font-semibold text-white bg-amber-600 rounded-lg px-3 py-1.5 disabled:opacity-40">
            {busy ? 'Sending…' : 'Alert Compliance'}
          </button>
        </div>
      )}
    </div>
  );
}
